// @flow
import React, { useState, useEffect } from 'react';
import type { Node } from 'react';
import styled from 'styled-components';
import { toSvg } from 'jdenticon';
import SVG from 'react-inlinesvg';
import { DesktopOnly } from './layout/Breakpoints';

const MainCardPool = styled.div`
  display: flex;
  padding: 15px 0;
  .card-image {
    width: 45px;
    min-width: 45px;
    margin-right: 15px;
    img {
      width: 100%;
    }
  }
  .card-info {
    display: flex;
    flex-wrap: wrap;
    color: #1a44b7;
    font-size: 16px;
    letter-spacing: 0;
    line-height: 22px;
    a {
      display: block;
    }
    i {
      font-size: 16px;
    }
  }
  .name {
    margin-right: 8px;
    margin-bottom: 4px;
  }
  .social-media {
    display: flex;
    flex: 1 1 60%;
    a {
      margin-right: 10px;
      font-size: 20px;
    }
  }
  .id {
    color: #676970;
    line-height: 22px;
    font-size: 14px;
    margin-right: 13px;
  }
`;
type Props = {|
  avatar: ?string,
  fullname: ?string,
  id: string,
  bech: string,
  tickerName: ?string,
  name: ?string,
  links: {|
    tw: ?string,
    tg: ?string,
    fb: ?string,
    yt: ?string,
    tc: ?string,
    di: ?string,
    gh: ?string,
    homepage?: ?string,
    icon: ?string,
  |},
|};

function StakingPoolCard({ id, bech, avatar, tickerName, name, links, fullname }: Props): Node {
  const [avatarImageLoaded, setAvatarImageLoaded] = useState(false);
  const [avatarImageError, setAvatarImageError] = useState(false);

  useEffect(() => {
    // Reset states when avatar changes
    setAvatarImageLoaded(false);
    setAvatarImageError(false);
  }, [avatar]);

  function truncateString(string: string): string {
    if (string.length <= 8) {
      return string;
    }
    return `${string.substring(0, 6)}...${string.slice(string.length - 2)}`;
  }

  const twitter = links && links.tw;
  const telegram = links && links.tg;
  const facebook = links && links.fb;
  const youtube = links && links.yt;
  const twitch = links && links.tc;
  const discord = links && links.di;
  const github = links && links.gh;
  const websiteUrl =
    fullname == null ? undefined : fullname.match(/(https?:\/\/[^\s]+"?utm_source=cexplorer.io)/g);

  // only show avatar if it is loaded and not errored
  const showAvatar = avatar && avatarImageLoaded && !avatarImageError;

  return (
    <MainCardPool>
      <div className="card-image">
        <>
          {
            // Try loading avatar if URL present at all
            avatar && (
              <img
                src={avatar}
                alt=""
                style={{ ...(showAvatar ? {} : { display: 'none' }) }}
                onLoad={() => setAvatarImageLoaded(true)}
                onError={() => setAvatarImageError(true)}
              />
            )
          }
          {
            // Display default jdenticon if avatar not present or not loaded
            !showAvatar && (
              <SVG src={toSvg(id, 42)} />
            )
          }
        </>
      </div>
      <div className="card-info">
        <a
          href={`https://cexplorer.io/pool/${bech}`}
          className="name"
          target="_blank"
          rel="noreferrer noopener"
        >
          {tickerName ? <>[{tickerName}] </> : null}
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: name || '' }} />
        </a>
        <DesktopOnly>
          <span className="id"> {truncateString(id)}</span>
        </DesktopOnly>
        <span className="social-media">
          {twitter ? (
            <a href={`https://twitter.com/${twitter}`} target="_blank" rel="noreferrer noopener">
              <i className="fa fa-twitter" />
            </a>
          ) : null}
          {telegram ? (
            <a href={`https://t.me/${telegram}`} target="_blank" rel="noreferrer noopener">
              <i className="fa fa-telegram" />
            </a>
          ) : null}
          {facebook ? (
            <a href={`https://fb.me/${facebook}`} target="_blank" rel="noreferrer noopener">
              <i className="fa fa-facebook" />
            </a>
          ) : null}
          {youtube ? (
            <a href={`https://youtube.com/${youtube}`} target="_blank" rel="noreferrer noopener">
              <i className="fa fa-youtube" />
            </a>
          ) : null}
          {twitch ? (
            <a href={`https://twitch.com/${twitch}`} target="_blank" rel="noreferrer noopener">
              <i className="fa fa-twitch" />
            </a>
          ) : null}
          {discord ? (
            <a href={`https://discord.gg/${discord}`} target="_blank" rel="noreferrer noopener">
              <i className="fa fa-server" />
            </a>
          ) : null}
          {github ? (
            <a href={`https://github.com/${github}`} target="_blank" rel="noreferrer noopener">
              <i className="fa fa-github-alt" />
            </a>
          ) : null}
          {websiteUrl ? (
            <a href={websiteUrl} target="_blank" rel="noreferrer noopener">
              <i className="fa fa-link" />
            </a>
          ) : null}
        </span>
      </div>
    </MainCardPool>
  );
}

export default StakingPoolCard;
